<template>
  <BaseForm
    v-if="user"
    ref="baseForm"
    :title="'User'"
    :data-object="transformedUser"
    :content-type="'profile'"
    :completed-location="completedLocation"
    :on-update-finished="refreshAvatar"
  >
    <template #content>
      <v-tex-field
        v-if="user.kind !== 'machine'"
        v-model.trim="user.first_name"
        label="First Name"
        :disabled="loading"
        :loading="loading"
        placeholder="First Name"
        icon="account"
      />
      <v-text-field
        v-if="user.kind !== 'machine'"
        v-model.trim="user.last_name"
        label="Surname"
        :disabled="loading"
        :loading="loading"
        placeholder="Surname"
        icon="account"
      />

      <v-sheet>
        <v-text-field
          v-model.trim="user.username"
          label="Benutzername"
          :disabled="loading || props.id !== '0'"
          :loading="loading"
          placeholder="Benutzername"
          icon="account"
        />

        <v-sheet v-if="user.kind !== 'machine'" expanded>
          <v-text-field
            v-model.trim="user.email"
            :disabled="loading || props.id !== '0'"
            :loading="loading"
            placeholder="E-Mail"
            type="email"
            icon="email"
          />

          <v-btn
            v-if="props.id !== '0' && isOwnProfile"
            prepend-icon="mdi-email"
            class="button"
            @click="
              router.push({
                name: 'request_change_email',
                params: { id: user.id },
              })
            "
          >
            {{ $t('models.auth.changeEmail') }}
          </v-btn>
        </v-sheet>
      </v-sheet>

      <v-select
        v-model="user.kind"
        color="primary"
        :items="[
          { title: 'Human', value: 'human' },
          { title: 'Machine', value: 'machine' },
        ]"
        label="User kind *"
        :disabled="props.id !== '0'"
        :loading="loading"
        placeholder="Human"
        @update:model-value="kindChanged"
      />

      <v-switch
        v-if="canUpdate2fa"
        v-model="user.require_vpn_2fa"
        label="VPN 2FA"
      />

      <div v-if="props.id !== '0' && canAddToken" class="card">
        <div class="card-header">
          <div class="card-header-title">Token list</div>
        </div>
        <div class="card-content">
          <TokenList :user="profileId" />
        </div>
      </div>

      <v-btn
        v-if="canResetPin"
        class="is-outlined"
        @click="resetPinCounters(user)"
      >
        <span class="icon is-large">
          <i class="mdi mdi-replay mdi-24px" />
        </span>
        <span>Reset Pin Counters</span>
      </v-btn>

      <AvatarUploadForm ref="uploadForm" :current-avatar="originalAvatar" />
    </template>
  </BaseForm>
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref, withDefaults } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSnackbar } from 'vuetify-use-dialog'
import { useStore } from 'vuex'

import { apiClient } from '@/api/ApiClient'
import AvatarUploadForm from '@/components/common/AvatarUploadForm.vue'
import BaseForm from '@/components/common/BaseForm.vue'
import { Profile } from '@/models/core/profile'

import { adminRouteName } from '../../app'
import TokenList from './TokenList.vue'

const route = useRoute()
const router = useRouter()
const createSnackbar = useSnackbar()

type UserFormProps = {
  id?: string
  kind?: string
}

const props = withDefaults(defineProps<UserFormProps>(), {
  id: '',
  kind: 'human',
})

const store = useStore()

const context = computed(() => store.state.global.context)
const baseForm: Ref<typeof BaseForm> = ref(null)
const uploadForm: Ref<typeof AvatarUploadForm> = ref(null)
const user: Ref<Profile> = ref(null)
const loading: Ref<boolean> = ref(false)
const canAddToken: Ref<boolean> = ref(false)
const originalAvatar: Ref<string> = ref(null)

onMounted(() => {
  loading.value = true

  apiClient.get('profile', profileId.value).then((response) => {
    originalAvatar.value = response.avatar
    user.value = response
    if (profileId.value === '0') {
      user.value.kind = props.kind
    }
    loading.value = false
    canAddToken.value = response._permissions.add_token
  })
})

const profileId = computed(() => {
  if (props.id === '') {
    // when we are entering the component via 'user-profile' route which does not have id parameter
    return store.state.global.profileId
  } else {
    return props.id
  }
})

function refreshAvatar() {
  if (profileId.value === store.state.global.profileId) {
    apiClient.get('profile', profileId.value).then((response) => {
      store.commit('global/setProfile', response)
    })
  }
}

const transformedUser = computed(() => {
  if (profileId.value === '0') {
    user.value.organisation = context.value.selection.organisation.id
  }
  if (uploadForm?.value !== null) {
    if (uploadForm?.value.avatar) {
      user.value.avatar = uploadForm.value.avatar
    }
  }
  return user.value
})

function kindChanged(kind: string) {
  if (isMachine.value) {
    user.value.email = ''
    user.value.first_name = ''
    user.value.last_name = ''
  }
}

const isOwnProfile = computed(() => {
  return profileId.value === store.state.global.profileId
})

const canResetPin = computed(() => {
  return (
    profileId.value !== '0' &&
    !isMachine.value &&
    !isOwnProfile.value &&
    user.value._permissions &&
    user.value._permissions.reset_pin_counter
  )
})

const canUpdate2fa = computed(() => {
  return (
    !isMachine.value &&
    !isOwnProfile.value &&
    (profileId.value === '0' ||
      (user.value._permissions && user.value._permissions.update_2fa))
  )
})

const isMachine = computed(() => {
  return user.value.kind === 'machine'
})

function resetPinCounters(profile: Profile) {
  apiClient
    .resetPinCounters(profile)
    .then(() => {
      createSnackbar({
        text: 'Successfully reset!',
        snackbarProps: { color: 'green-darken-3' },
      })
    })
    .catch((error) => {
      baseForm.value.handleError(error)
    })
}

const completedLocation = computed(() => {
  if (route.params.app_handle) {
    return {
      name: adminRouteName('profile-list'),
    }
  } else {
    return {
      name: '/',
    }
  }
})
</script>

<style lang="scss" scoped>
.modal {
  color: black;
}
</style>
